import _styled, { default as _styled2, default as _styled3 } from "styled-components";
import React from "react";
import dynamic from "next/dynamic";
import type { DownloadTooltipView, DownloadVersionOption } from "~/features/downloads/adapter";
import { useDownloads } from "~/features/downloads/context";
import { FilterBar, type Filter } from "~/features/filter/filter-bar";
import { getThemeStyle } from "~/features/ui/get-theme-style";
import { Wrap } from "~/features/ui/wrap";
import { hasItems } from "~/features/app/utils";
const FilterTooltip = dynamic(() => import("~/features/downloads/filter-tooltip").then(mod => mod.FilterTooltip), {
  ssr: false
});
type Props = {
  hardwareOptions: DownloadVersionOption[];
  firmwareOptions: DownloadVersionOption[];
  tooltips: DownloadTooltipView[];
};

/**
 * Filters one dropdown's options by the active selection in the other, using
 * the compatibility arrays (empty = compatible with everything). Mirrors the
 * legacy `mapOptions` cross-filtering.
 */
function compatibleOptions(options: DownloadVersionOption[], activeOther: string) {
  return options.filter(option => option.compatibleSlugs.length === 0 || option.compatibleSlugs.includes(activeOther)).map(option => ({
    label: option.title,
    value: option.slug
  }));
}
export function DownloadsFilters(props: Props) {
  const {
    hardwareOptions: hardwareVersions,
    firmwareOptions: firmwareVersions,
    tooltips
  } = props;
  const themeStyle = getThemeStyle({
    mode: "light"
  });
  const {
    activeFirmwareVersion,
    activeHardwareVersion,
    setActiveFirmwareVersion,
    setActiveHardwareVersion
  } = useDownloads();
  const hardwareOptions = compatibleOptions(hardwareVersions, activeFirmwareVersion);
  const firmwareOptions = compatibleOptions(firmwareVersions, activeHardwareVersion);

  // Reset to the first available option whenever the active selection is no
  // longer compatible with the other filter.
  React.useEffect(() => {
    if (!hardwareOptions.some(option => option.value === activeHardwareVersion) && hardwareOptions[0]?.value) {
      setActiveHardwareVersion(hardwareOptions[0].value);
    }
  }, [hardwareOptions, activeHardwareVersion, setActiveHardwareVersion]);
  React.useEffect(() => {
    if (!firmwareOptions.some(option => option.value === activeFirmwareVersion) && firmwareOptions[0]?.value) {
      setActiveFirmwareVersion(firmwareOptions[0].value);
    }
  }, [firmwareOptions, activeFirmwareVersion, setActiveFirmwareVersion]);
  function onChange(filterType: string, value: string) {
    if (filterType === "Hardware version") {
      setActiveHardwareVersion(value);
    }
    if (filterType === "Firmware version") {
      setActiveFirmwareVersion(value);
    }
  }
  const filters: Filter[] = [{
    title: "Hardware version",
    options: hardwareOptions,
    type: "select",
    value: activeHardwareVersion,
    style: {
      "width": "100%",
      "maxWidth": "468px"
    },
    tooltip: hasItems(tooltips) ? <FilterTooltip tooltip={tooltips.find(tooltip => tooltip.target === "hardware")} /> : undefined
  }, {
    title: "Firmware version",
    options: firmwareOptions,
    type: "select",
    value: activeFirmwareVersion,
    style: {
      "width": "100%",
      "maxWidth": "468px"
    },
    tooltip: hasItems(tooltips) ? <FilterTooltip tooltip={tooltips.find(tooltip => tooltip.target === "firmware")} /> : undefined
  }];
  return <_StyledDiv $_css={[{
    "borderTopWidth": "1px",
    "borderBottomWidth": "1px"
  }, themeStyle.border, themeStyle.background]}>
      <Wrap mode="light">
        <_StyledDiv2>
          <_StyledDiv3 $_css2={[{
          "position": "relative",
          "zIndex": "10",
          "paddingLeft": "1.6rem",
          "paddingRight": "1.6rem",
          "paddingTop": "2.4rem",
          "paddingBottom": "2.4rem",
          "@media (min-width: 1024px)": {
            "borderLeftWidth": "1px",
            "borderRightWidth": "1px",
            "paddingLeft": "4rem",
            "paddingRight": "4rem"
          }
        }, themeStyle.border, themeStyle.background]}>
            <FilterBar filters={filters} onChange={onChange} />
          </_StyledDiv3>
        </_StyledDiv2>
      </Wrap>
    </_StyledDiv>;
}
var _StyledDiv = /*#__PURE__*/_styled("div").withConfig({
  componentId: "sc-sf-1hrju03-0"
})`${p => p.$_css}`;
var _StyledDiv2 = _styled2("div").withConfig({
  componentId: "sc-sf-1hrju03-1"
})({
  "@media (min-width: 1024px)": {
    "paddingLeft": "4rem",
    "paddingRight": "4rem"
  }
});
var _StyledDiv3 = /*#__PURE__*/_styled3("div").withConfig({
  displayName: "filters___StyledDiv3",
  componentId: "sc-1hvq0wj-0"
})(["", ""], p => p.$_css2);